import {
  Center,
  chakra,
  Checkbox,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { langStore, translate } from "@noli/business";
import { observer } from "mobx-react-lite";
import React, { useCallback, useState } from "react";
import { themeStore } from "@/stores/theme";
import { OptimizerType } from "@/types/creator";
import classNames from "classnames";
import { genBlock } from "@/utils/bem";
import {
  defaultOptimizers,
  modelDefaultOptimizers,
  optimizerList,
  optimizersMap,
  optimizerTranlations,
} from "@/modules/editor/constant";
import { Editor_Words } from "@/utils/lang/editor";
import { setNegativePrompt, statusStore } from "@/stores/status";
import Icon from "@/components/Icon";
import { ReactComponent as ArrowSvg } from "@/assets/icons/arrow-down.svg";
import "./style.scss";
import { isNoliboxModel } from "../ModelSwitch/util";

const block = genBlock("c-optimizer-selector");
const IOptimizerSelector: React.FC<{
  value: OptimizerType[];
  setValue: (value: OptimizerType[]) => void;
  className?: string;
}> = ({ value, setValue, className }) => {
  const lang = langStore.tgt;
  const { modelType } = statusStore;
  const { panelBg } = themeStore.styles;
  const panelW = 190;
  const panelH = 103;
  const textH = 24;

  function handleChange(itemValue: OptimizerType) {
    const newValue: OptimizerType[] = value.concat();
    if (value.includes(itemValue)) {
      newValue.splice(value.indexOf(itemValue), 1);
    } else {
      newValue.push(itemValue);
    }
    setValue(newValue);
    setNegativePrompt(newValue.map((optimizeType) => optimizersMap[optimizeType].value).join(", "));
  }

  function selectAll() {
    setValue(optimizerList.map((item) => item.value));
  }

  const valueFormatter = useCallback(
    (value: OptimizerType[]) => {
      const _defaultOptimizers =
        (isNoliboxModel(modelType) && modelDefaultOptimizers[modelType]) || defaultOptimizers;
      const isDefault =
        value.length === _defaultOptimizers.length &&
        (!_defaultOptimizers.length ||
          (value as OptimizerType[]).every((str) => _defaultOptimizers.includes(str)));

      return translate(
        isDefault ? Editor_Words["value-default"] : Editor_Words["value-custom"],
        lang,
      );
    },
    [modelType, lang],
  );

  return (
    <Center className={classNames(className, block())} w="100%">
      <Popover
        isLazy
        lazyBehavior="keepMounted"
        placement="right-start"
        boundary={document.body}
        matchWidth
        eventListeners={false}
        preventOverflow
        flip
        gutter={24}
        modifiers={[
          {
            name: "topLogger",
            enabled: true,
            phase: "main",
            fn: ({ state }) => {
              state.styles.popper.zIndex = "1000";
              return state;
            },
          },
        ]}
      >
        <PopoverTrigger>
          <Flex
            tabIndex={0}
            cursor="pointer"
            w="100%"
            h={`${textH}px`}
            color="text.title"
            fontSize="14px"
            align="center"
            _hover={{
              color: "#3fc9a8",
            }}
          >
            <Text>
              {translate(Editor_Words["optimizer"], lang)} : {valueFormatter(value)}
            </Text>
            <Spacer />
            <Icon squared={false} className={block({ e: "arrow_icon" })} svg={ArrowSvg} />
          </Flex>
        </PopoverTrigger>
        <PopoverContent
          border="none"
          bg="#3A3A3A"
          rounded="4px"
          w={`${panelW}px`}
          _focusVisible={{
            border: "none",
            boxShadow: "none",
          }}
          boxShadow="none"
          zIndex={500}
          className={block("popup")}
          outline="none"
        >
          <PopoverBody p="0px" w="fit-content">
            <Flex
              w={`${panelW}px`}
              minH={`${panelH}px`}
              bg={panelBg}
              rounded="4px"
              fontSize="12px"
              p="8px 18px 12px 12px"
              flexDirection="column"
              className={block("popup-content")}
            >
              <Flex justifyContent="flex-end" mb="8px">
                <Text color="brand.primary" cursor="pointer" onClick={selectAll}>
                  {translate(Editor_Words["select-all"], lang)}
                </Text>
              </Flex>
              <Flex w="100%" gap="8px" wrap="wrap" align="center" justifyContent="space-between">
                {optimizerList.map((item) => (
                  <Checkbox
                    colorScheme="brand"
                    key={item.value}
                    color="text.placeholder"
                    borderColor="line.darkGray"
                    className="c-checkbox"
                    fontWeight={500}
                    // size="sm"
                    fontSize="sm"
                    value={item.value}
                    isChecked={value.includes(item.value)}
                    onChange={(e) => handleChange(e.target.value as OptimizerType)}
                  >
                    {optimizerTranlations[item.value][lang]}
                  </Checkbox>
                ))}
              </Flex>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Center>
  );
};

export const OptimizerSelector = chakra(observer(IOptimizerSelector));
